import type { ColorScheme } from './colorSchemeStore';
import { getColorScheme } from './colorSchemeStore';
import { darkPalette, lightPalette } from './palette';

// GoJS and Chart.js draw on canvas and need resolved colours rather than
// CSS variables. Unit panels follow the scheme like the explorer cards do;
// the units' equipment cards stay light in both schemes.

const diagramColors = {
  light: {
    background: '#FAFAFA',
    gridLine: '#E0E0E0',
    // Strokes drawn directly on the canvas, e.g. the print window frame.
    ink: '#000000',
    watermark: '#E0E0E0',
    hoverFill: '#E6F5FF95',
    placeholderFill: '#EEEEEE',
    placeholderStroke: '#9E9E9E',
    textBoxFill: '#FFFFFF',
    textBoxText: '#000000',
    textBoxBorder: '#000000',
    panelFill: '#FFFFFF',
    panelBorder: lightPalette.grey['300'],
    panelText: lightPalette.grey['900'],
    panelSubtleText: lightPalette.grey['800'],
  },
  dark: {
    background: '#1E1E1E',
    gridLine: '#333333',
    ink: '#E0E0E0',
    watermark: '#3A3A3A',
    hoverFill: '#E6F5FF',
    placeholderFill: '#2C2C2C',
    placeholderStroke: '#757575',
    textBoxFill: '#2C2C2C',
    textBoxText: '#E0E0E0',
    textBoxBorder: '#9E9E9E',
    panelFill: darkPalette.surface.raised,
    panelBorder: darkPalette.grey['300'],
    panelText: darkPalette.grey['900'],
    panelSubtleText: darkPalette.grey['800'],
  },
};

export type DiagramColors = (typeof diagramColors)['light'];

// Lets exports render the diagram in light colours without changing the
// scheme for the rest of the app.
let diagramSchemeOverride: ColorScheme | null = null;

export const getDiagramColorScheme = (): ColorScheme =>
  diagramSchemeOverride ?? getColorScheme();

export const getDiagramColors = (
  scheme: ColorScheme = getDiagramColorScheme(),
): DiagramColors => diagramColors[scheme];

export const withDiagramColorScheme = <T>(
  scheme: ColorScheme,
  render: () => T,
): T => {
  const previous = diagramSchemeOverride;
  diagramSchemeOverride = scheme;
  try {
    return render();
  } finally {
    diagramSchemeOverride = previous;
  }
};

// Light values match Chart.js defaults so light mode looks unchanged.
const chartColors = {
  light: {
    text: '#666666',
    grid: 'rgba(0, 0, 0, 0.1)',
    ink: '#101010',
    primaryLine: '#0069AB',
    primaryFill: '#EBF7FF80',
    zoneLine: '#003C7D',
    labelBackground: '#FFFFFF',
    labelText: '#424242',
    overlayText: '#000000C4',
    tooltipBackground: 'rgba(0, 0, 0, 0.8)',
    tooltipText: '#FFFFFF',
  },
  dark: {
    text: '#BDBDBD',
    grid: 'rgba(255, 255, 255, 0.12)',
    ink: '#E0E0E0',
    primaryLine: '#64B5F6',
    primaryFill: '#327DFF33',
    zoneLine: '#94BBFF',
    labelBackground: '#272727',
    labelText: '#E0E0E0',
    overlayText: '#FFFFFFC4',
    tooltipBackground: 'rgba(97, 97, 97, 0.92)',
    tooltipText: '#FFFFFF',
  },
};

export type ChartColors = (typeof chartColors)['light'];

export const getChartColors = (
  scheme: ColorScheme = getColorScheme(),
): ChartColors => chartColors[scheme];

// Series colours stored in chart data. "Ink" is resolved per scheme when
// drawn so stored datasets don't change; the primary blue reads on both.
export const SERIES_PRIMARY = '#327DFF';
export const SERIES_INK = '#101010';

export const resolveSeriesColor = (color: string, colors: ChartColors) =>
  color === SERIES_INK ? colors.ink : color;
